import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';

import * as customerActions from '@store/customer/customer.actions';
import * as customerSelectors from '@store/customer/customer.selectors';
import {
  IAggregatorValidationResponse,
  IAvailableScore,
  ICustomerCard,
  ICustomerValidationResponse,
  IMaxInstallmentValidationResponse,
  IUnifiedScore,
} from '../core/models/customer.model';
import {
  ICreateLoan,
  ICreatePreLoan,
  ICustomer,
  ICustomerValidate,
  IInstallmentsConfig,
} from '../core/models/customer.model';

@Injectable({ providedIn: 'root' })
export class CustomerFacade {
  public customer$: Observable<ICustomer>;
  public preLoan$: Observable<IInstallmentsConfig[]>;
  public isLoading$: Observable<boolean>;
  public isLoadingPreLoan$: Observable<boolean>;
  public error$: Observable<boolean>;
  public redirectUrl$: Observable<string>;
  public preLoanId$: Observable<string>;
  public customerValidation$: Observable<ICustomerValidationResponse>;
  public customerCard$: Observable<ICustomerCard>;
  public availableScore$: Observable<IUnifiedScore>;
  public aggregatorValidation$: Observable<IAggregatorValidationResponse>;
  public maxInstallmentValidation$: Observable<IMaxInstallmentValidationResponse>;
  public debitDecisionValidation$: Observable<any>;

  constructor(private _store: Store) {
    this.customer$ = this._store.select(customerSelectors.selectCustomer);
    this.preLoan$ = this._store.select(customerSelectors.selectPreLoan);
    this.isLoading$ = this._store.select(customerSelectors.selectIsLoading);
    this.isLoadingPreLoan$ = this._store.select(customerSelectors.selectIsLoadingPreLoan);
    this.error$ = this._store.select(customerSelectors.selectIsError);
    this.redirectUrl$ = this._store.select(customerSelectors.selectRedirectUrl);
    this.preLoanId$ = this._store.select(customerSelectors.selectPreLoanId);
    this.customerValidation$ = this._store.select(customerSelectors.selectCustomerValidation);
    this.customerCard$ = this._store.select(customerSelectors.selectCustomerCard);
    this.availableScore$ = this._store.select(customerSelectors.selectAvailableScore);
    this.aggregatorValidation$ = this._store.select(customerSelectors.selectAggregatorValidation);
    this.maxInstallmentValidation$ = this._store.select(
      customerSelectors.selectMaxInstallmentValidation
    );
    this.debitDecisionValidation$ = this._store.select(
      customerSelectors.selectDebitDecisionValidation
    );
  }

  public requestCustomerInformation() {
    this._store.dispatch(customerActions.requestCustomerInformation());
  }

  public setCustomerInformation(customer: ICustomer) {
    this._store.dispatch(customerActions.setCustomerInformation({ customer }));
  }

  public redirectUserToScore() {
    this._store.dispatch(customerActions.redirectUserToScore());
  }

  public listenCustomerValidation() {
    this._store.dispatch(customerActions.listenCustomerValidation());
  }

  public successListenCustomerValidation(validation: ICustomerValidationResponse) {
    this._store.dispatch(customerActions.successListenCustomerValidation({ validation }));
  }

  public requestPreLoan() {
    this._store.dispatch(customerActions.requestPreLoan());
  }

  public createPreLoan(createPreLoan: ICreatePreLoan) {
    this._store.dispatch(customerActions.createPreLoan({ createPreLoan }));
  }

  public redirectToEcommerce(portal: boolean) {
    this._store.dispatch(customerActions.redirectToEcommerce({ portal }));
  }

  public getCustomerCard() {
    this._store.dispatch(customerActions.getCustomerCard());
  }

  public setCustomerValidate(validation: ICustomerValidationResponse) {
    this._store.dispatch(customerActions.setCustomerValidation({ validation }));
  }

  public successGetCustomerCard(cardInfo: ICustomerCard) {
    this._store.dispatch(customerActions.successGetCustomerCard({ cardInfo }));
  }

  public updateIsLoadingPreLoan(isLoadingPreLoan: boolean) {
    this._store.dispatch(customerActions.updateIsLoadingPreLoan({ isLoadingPreLoan }));
  }

  public checkPendingPayments(route: string) {
    this._store.dispatch(customerActions.checkPendingPayments({ route }));
  }

  public requestAvailableScore(route?: string) {
    this._store.dispatch(customerActions.requestAvailableScore({ route }));
  }

  public requestCustomerScore(): void {
    this._store.dispatch(customerActions.requestCustomerScore());
  }

  public requestAggregatorValidation(): void {
    this._store.dispatch(customerActions.requestAggregatorValidation());
  }

  public requestMaxInstallmentValidation(): void {
    this._store.dispatch(customerActions.requestMaxInstallmentValidation());
  }

  public requestDebitDecisionValidation(): void {
    this._store.dispatch(customerActions.requestDebitDecisionValidation());
  }

  public validateCustomerCard(cardInfo: ICustomerCard): void {
    this._store.dispatch(customerActions.validateCustomerCard({ cardInfo }));
  }
}
